// Zustand Auth Store - Persistent, Reliable Authentication
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ROLE_LABELS } from '../types/rbacImproved';
import { PermissionManager } from '../utils/permissionManager';
import { PermissionService } from '../utils/permissions';
import { getApiBaseUrl } from '../utils/apiClient';
import { activityTracker } from '../services/activityTracker';
// Custom storage that can switch between localStorage and sessionStorage
const createDualStorage = () => {
    return {
        getItem: (name) => {
            // CRITICAL FIX: Check for most recent token by comparing timestamps
            const sessionValue = sessionStorage.getItem(name);
            const localValue = localStorage.getItem(name);
            // If both exist, use the one with the most recent token
            if (sessionValue && localValue) {
                try {
                    const sessionData = JSON.parse(sessionValue);
                    const localData = JSON.parse(localValue);
                    // Compare token expiry times to determine which is newer
                    const sessionExpiry = sessionData?.state?.tokenExpiry || 0;
                    const localExpiry = localData?.state?.tokenExpiry || 0;
                    // Use the one with the later expiry (newer token)
                    if (localExpiry > sessionExpiry) {
                        console.log('🔐 Using localStorage token (newer)');
                        return localValue;
                    }
                    else {
                        console.log('🔐 Using sessionStorage token (newer)');
                        return sessionValue;
                    }
                }
                catch (e) {
                    // If parsing fails, prefer localStorage (remember me)
                    console.log('🔐 Parse error, defaulting to localStorage');
                    return localValue || sessionValue;
                }
            }
            // Return whichever exists
            return localValue || sessionValue;
        },
        setItem: (name, value) => {
            // Storage location will be set during login based on rememberMe flag
            // For now, just use localStorage (will be overridden by login logic)
            localStorage.setItem(name, value);
        },
        removeItem: (name) => {
            // Clear from both storages
            sessionStorage.removeItem(name);
            localStorage.removeItem(name);
        }
    };
};
export const useAuthStore = create()(persist((set, get) => ({
    // Initial state
    user: null,
    isAuthenticated: false,
    isLoading: false,
    permissions: [],
    error: null,
    accessToken: null,
    refreshToken: null,
    tokenExpiry: null,
    // Permission helpers
    hasPermission: (permission) => {
        const { permissions } = get();
        return permissions.includes(permission);
    },
    hasAnyPermission: (requiredPermissions) => {
        const { permissions } = get();
        return requiredPermissions.some(p => permissions.includes(p));
    },
    hasAllPermissions: (requiredPermissions) => {
        const { permissions } = get();
        return requiredPermissions.every(p => permissions.includes(p));
    },
    // Set user and calculate permissions
    setUser: (user) => {
        const permissions = PermissionManager.calculateEffectivePermissions(user.roleLevel, [], // Additional permissions (from overrides)
        [], // Revoked permissions (from overrides)
        [] // Temporary permissions (from overrides)
        );
        console.log('🔐 Zustand - User Set:', {
            userId: user.id,
            email: user.email,
            role: user.role,
            roleLevel: user.roleLevel,
            roleName: ROLE_LABELS[user.roleLevel],
            totalPermissions: permissions.length,
            hasAtelierPermission: permissions.includes('page.atelier.view'),
            hasUserManagementPermission: permissions.includes('page.admin.users.view')
        });
        set({
            user,
            isAuthenticated: true,
            permissions,
            error: null,
            isLoading: false
        });
    },
    // Set tokens
    setTokens: (accessToken, refreshToken) => {
        const tokenPayload = parseJWT(accessToken);
        const expiry = tokenPayload?.exp ? tokenPayload.exp * 1000 : null;
        set({
            accessToken,
            refreshToken: refreshToken || get().refreshToken,
            tokenExpiry: expiry
        });
        // NOTE: Removed legacy localStorage.setItem('accessToken', 'refreshToken')
        // Zustand persist middleware handles all storage automatically
        // Writing to legacy keys caused zombie tokens and storage mismatches
        console.log('🔐 Zustand - Tokens Set:', {
            hasAccessToken: !!accessToken,
            hasRefreshToken: !!(refreshToken || get().refreshToken),
            tokenExpiry: expiry ? new Date(expiry).toLocaleString() : 'Unknown'
        });
    },
    // Login function
    login: async (email, password, rememberMe = false) => {
        set({ isLoading: true, error: null });
        try {
            // Use apiClient for consistent URL construction across environments
            const response = await fetch(`${getApiBaseUrl()}/auth-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include', // Azure Portal CORS configured with Allow-Credentials
                body: JSON.stringify({ email, password, rememberMe })
            });
            const data = await response.json();
            if (data.success) {
                // Persistent logging that survives page refresh
                const debugData = {
                    timestamp: new Date().toISOString(),
                    environment: process.env.NODE_ENV,
                    hasAccessToken: !!data.accessToken,
                    hasRefreshToken: !!data.refreshToken,
                    userRole: data.user.role,
                    userEmail: data.user.email,
                    userId: data.user.id,
                    userStatus: data.user.status,
                    originalRole: data.user.role,
                    processedRole: data.user.role || 'DEALER_APPLICANT',
                    calculatedRoleLevel: PermissionService.getRoleLevel(data.user.role || 'DEALER_APPLICANT'),
                    expectedSuperAdmin: PermissionService.getRoleLevel(data.user.role || 'DEALER_APPLICANT') === 10
                };
                // Store in localStorage for persistence
                localStorage.setItem('auth-debug-last-login', JSON.stringify(debugData));
                // Also log to console (will be lost but good for immediate debugging)
                console.log('🔐 PERSISTENT LOGIN DEBUG:', debugData);
                // Set tokens
                get().setTokens(data.accessToken, data.refreshToken);
                // Set user
                const userRole = data.user.role || 'DEALER_APPLICANT';
                const userRoleLevel = PermissionService.getRoleLevel(userRole);
                get().setUser({
                    id: data.user.id,
                    email: data.user.email,
                    role: userRole,
                    roleLevel: userRoleLevel,
                    emailVerified: data.user.emailVerified,
                    mfaEnabled: data.user.mfaEnabled,
                    status: data.user.status,
                    profile: data.user.profile
                });
                // CRITICAL: Force immediate persistence before any hard refresh
                const currentState = get();
                const persistData = {
                    user: currentState.user,
                    accessToken: currentState.accessToken,
                    refreshToken: currentState.refreshToken,
                    tokenExpiry: currentState.tokenExpiry
                };
                // Choose storage based on rememberMe flag
                const storage = rememberMe ? localStorage : sessionStorage;
                const storageKey = 'royal-gems-auth';
                const storageType = rememberMe ? 'localStorage (persistent)' : 'sessionStorage (session-only)';
                console.log(`🔐 Remember Me: ${rememberMe} → Using ${storageType}`);
                // Clear the other storage to prevent conflicts
                if (rememberMe) {
                    sessionStorage.removeItem(storageKey);
                }
                else {
                    localStorage.removeItem(storageKey);
                }
                // Force immediate save using Zustand's exact format
                storage.setItem(storageKey, JSON.stringify({
                    state: persistData,
                    version: 0
                }));
                console.log(`✅ Auth saved to ${storageType}`);
                activityTracker.trackAction('user-logged-in', `User logged in: ${email}`, { email, role: userRole, rememberMe });
                // Also force a manual flush of the store to ensure persistence
                setTimeout(() => {
                    const verifyState = get();
                    storage.setItem(storageKey, JSON.stringify({
                        state: {
                            user: verifyState.user,
                            accessToken: verifyState.accessToken,
                            refreshToken: verifyState.refreshToken,
                            tokenExpiry: verifyState.tokenExpiry
                        },
                        version: 0
                    }));
                    console.log(`🔐 DOUBLE PERSISTENCE - Auth state verified in ${storageType}`);
                }, 100);
                return { success: true };
            }
            else {
                set({ error: data.error, isLoading: false });
                return { success: false, error: data.error };
            }
        }
        catch (error) {
            const errorMessage = 'Login failed. Please try again.';
            set({ error: errorMessage, isLoading: false });
            return { success: false, error: errorMessage };
        }
    },
    // Register function
    register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
            // Use apiClient for consistent URL construction across environments
            const response = await fetch(`${getApiBaseUrl()}/auth-register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include', // Azure Portal CORS configured with Allow-Credentials
                body: JSON.stringify(userData)
            });
            const data = await response.json();
            if (data.success) {
                // Auto-login after successful registration
                if (data.accessToken) {
                    get().setTokens(data.accessToken, data.refreshToken);
                    const userRole = data.user.role || 'DEALER_APPLICANT';
                    const userRoleLevel = PermissionService.getRoleLevel(userRole);
                    get().setUser({
                        id: data.user.id,
                        email: data.user.email,
                        role: userRole,
                        roleLevel: userRoleLevel,
                        emailVerified: data.user.emailVerified,
                        mfaEnabled: data.user.mfaEnabled,
                        status: data.user.status
                    });
                }
                return { success: true };
            }
            else {
                set({ error: data.error, isLoading: false });
                return { success: false, error: data.error };
            }
        }
        catch (error) {
            const errorMessage = 'Registration failed. Please try again.';
            set({ error: errorMessage, isLoading: false });
            return { success: false, error: errorMessage };
        }
    },
    // Logout function
    logout: () => {
        const currentUser = get().user;
        if (currentUser) {
            activityTracker.trackAction('user-logged-out', `User logged out: ${currentUser.email}`, { email: currentUser.email, role: currentUser.role });
            activityTracker.flush();
        }
        console.log('🔐 Zustand - Logout - Resetting to initial state');
        // Reset to exact initial state - no leftover values
        set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            permissions: [],
            error: null,
            accessToken: null,
            refreshToken: null,
            tokenExpiry: null
        });
        // Clear ALL storage keys (both localStorage and sessionStorage)
        localStorage.removeItem('royal-gems-auth'); // ✅ Zustand persist key
        sessionStorage.removeItem('royal-gems-auth'); // ✅ Zustand persist key
        localStorage.removeItem('accessToken'); // Legacy key (for compatibility)
        localStorage.removeItem('refreshToken'); // Legacy key (for compatibility)
        sessionStorage.removeItem('accessToken'); // Clear from session too
        sessionStorage.removeItem('refreshToken'); // Clear from session too
        console.log('🔐 Zustand - Logout complete - All storage cleared');
    },
    // Check authentication status (on app load)
    checkAuthStatus: async () => {
        console.log('🔐 Zustand - Checking auth status...');
        set({ isLoading: true });
        try {
            // Read from Zustand state only (not localStorage) to avoid race conditions
            const token = get().accessToken;
            const refreshToken = get().refreshToken;
            if (!token) {
                console.log('🔐 Zustand - No token found');
                set({ isLoading: false });
                return;
            }
            // Parse JWT token
            const tokenPayload = parseJWT(token);
            if (!tokenPayload) {
                console.log('🔐 Zustand - Invalid token format');
                get().logout();
                return;
            }
            // Check if token is expired (with 5-second grace period to prevent false positives)
            const GRACE_PERIOD_MS = 5000;
            if (tokenPayload.exp * 1000 < (Date.now() - GRACE_PERIOD_MS)) {
                console.log('🔐 Zustand - Token expired');
                get().logout();
                return;
            }
            // Set user from JWT immediately
            const userRole = tokenPayload.role || 'DEALER_APPLICANT';
            const userRoleLevel = PermissionService.getRoleLevel(userRole);
            get().setUser({
                id: tokenPayload.sub,
                email: tokenPayload.email,
                role: userRole,
                roleLevel: userRoleLevel,
                emailVerified: true, // Assume verified if token is valid
                mfaEnabled: false,
                status: 'active'
            });
            get().setTokens(token, refreshToken || undefined);
            console.log('🔐 Zustand - Auth restored from JWT');
        }
        catch (error) {
            console.error('🔐 Zustand - Auth check failed:', error);
            get().logout();
        }
    },
    // Refresh tokens
    refreshTokens: async () => {
        const timestamp = new Date().toISOString();
        const { refreshToken } = get();
        if (!refreshToken) {
            console.log(`🔐 [${timestamp}] Zustand - No refresh token available, cannot refresh`);
            return false;
        }
        console.log(`🔄 [${timestamp}] Zustand - Starting token refresh...`);
        try {
            const apiUrl = `${getApiBaseUrl()}/auth-refresh`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken })
            });
            console.log(`🔄 [${timestamp}] Zustand - Refresh response:`, {
                status: response.status,
                ok: response.ok,
                statusText: response.statusText
            });
            if (response.ok) {
                const data = await response.json();
                get().setTokens(data.accessToken, data.refreshToken);
                console.log(`✅ [${timestamp}] Zustand - Token refresh SUCCESS! New tokens stored.`);
                console.log(`✅ [${timestamp}] Zustand - User can continue session without re-login`);
                return true;
            }
            else {
                const errorData = await response.json().catch(() => ({}));
                console.error(`❌ [${timestamp}] Zustand - Token refresh FAILED:`, {
                    status: response.status,
                    error: errorData.error || response.statusText,
                    details: errorData
                });
                return false;
            }
        }
        catch (error) {
            console.error(`❌ [${timestamp}] Zustand - Token refresh EXCEPTION:`, {
                message: error instanceof Error ? error.message : String(error),
                name: error instanceof Error ? error.name : 'Unknown',
                error
            });
            return false;
        }
    },
    // Clear error
    clearError: () => set({ error: null }),
    // Debug helper
    getAuthDebugInfo: () => {
        const state = get();
        return {
            isAuthenticated: state.isAuthenticated,
            user: state.user ? {
                id: state.user.id,
                email: state.user.email,
                role: state.user.role,
                roleLevel: state.user.roleLevel
            } : null,
            permissionCount: state.permissions.length,
            hasAtelierPermission: state.hasPermission('page.atelier.view'),
            hasUserManagementPermission: state.hasPermission('page.admin.users.view'),
            tokenExpiry: state.tokenExpiry ? new Date(state.tokenExpiry).toLocaleString() : null,
            error: state.error
        };
    }
}), {
    name: 'royal-gems-auth',
    storage: createJSONStorage(() => createDualStorage()),
    // Only persist essential data
    partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        tokenExpiry: state.tokenExpiry
    }),
    // Restore permissions on hydration
    onRehydrateStorage: () => (state) => {
        // Persistent debug for rehydration
        const rehydrationDebug = {
            timestamp: new Date().toISOString(),
            hasState: !!state,
            hasUser: !!state?.user,
            userRole: state?.user?.role,
            userRoleLevel: state?.user?.roleLevel,
            userId: state?.user?.id
        };
        localStorage.setItem('auth-debug-rehydration', JSON.stringify(rehydrationDebug));
        console.log('🔐 REHYDRATION DEBUG:', rehydrationDebug);
        if (state?.user) {
            const permissions = PermissionManager.calculateEffectivePermissions(state.user.roleLevel, [], [], []);
            state.permissions = permissions;
            state.isAuthenticated = true;
            const finalState = {
                userRole: state.user.role,
                userRoleLevel: state.user.roleLevel,
                permissionCount: permissions.length,
                isAuthenticated: state.isAuthenticated
            };
            localStorage.setItem('auth-debug-final-state', JSON.stringify(finalState));
            console.log('🔐 Zustand - Rehydrated auth state:', finalState);
        }
        else {
            localStorage.setItem('auth-debug-final-state', JSON.stringify({ error: 'No user to rehydrate' }));
        }
    }
}));
// Helper function to parse JWT
function parseJWT(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return null;
        const payload = JSON.parse(atob(parts[1]));
        return payload;
    }
    catch (error) {
        console.error('JWT parse error:', error);
        return null;
    }
}
// Export convenience hooks
export const useAuth = () => useAuthStore();
export const useUser = () => useAuthStore(state => state.user);
export const usePermissions = () => useAuthStore(state => state.permissions);
export const useAuthActions = () => useAuthStore(state => ({
    login: state.login,
    logout: state.logout,
    checkAuthStatus: state.checkAuthStatus,
    refreshTokens: state.refreshTokens
}));
